import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";

import { type DealDataRoom } from "@vega-api/generated/types/dealDataRoom";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

export type EditDataRoomModalProps = {
  onClose: () => void;
  onConfirm: (name: string, url: string) => void;
  dataRoom: DealDataRoom;
  isUpdatePending?: boolean;
};

type FormValues = {
  name: string;
  url: string;
};

export const EditDataRoomModal = ({
  onClose,
  onConfirm,
  dataRoom,
  isUpdatePending,
}: EditDataRoomModalProps) => {
  const validationSchema = yup.object({
    name: yup.string().required(m.formValidation_requiredText()),
    url: yup
      .string()
      .required(m.formValidation_requiredText())
      .url(m.formValidation_invalidUrl()),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      name: dataRoom.dataRoomProvider.displayName,
      url: dataRoom.dataRoomUrl ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      onConfirm(values.name, values.url);
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionText: m.btn_confirm(),
        mainAction: formik.submitForm,
        mainActionVariant: "primary",
        disabled: !formik.isValid || !formik.dirty || isUpdatePending,
        secondaryActionText: m.common_cancel(),
        secondaryAction: handleClose,
        disabledSecondary: isUpdatePending,
      }}
      fluidHeight
      open
      setOpen={onClose}
      title={m.coInvestments_dataRoom_editModal_title()}
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="mt-6 w-full md:min-w-96 space-y-4">
            <InputField label={m.common_name()} name="name" withBorder />
            <InputField
              label={m.coInvestments_dataRoom_urlLabel()}
              name="url"
              placeholder="https://dataroom.com"
              type="url"
              withBorder
            />
          </div>
        </form>
      </FormikProvider>
    </ActionaryModal>
  );
};
